import type { SignupSchool, SportPrice } from '../../types/signup';
import { describeSchool, formatAnnualPrice, sportLabel } from './signupFormat';

/**
 * The school the email resolved to, and what a program there costs.
 *
 * Prices can differ by sport. When the caller passes more than one sport price and they differ,
 * each is listed with its sport; otherwise one price is quoted, whichever sport the coach picks.
 * A sport priced by plan (`fromSports`) quotes its cheapest plan as a "from" price.
 */
export const SignupSchoolSummary = ({
  school,
  annualPriceCents,
  sportPrices = [],
  fromSports = [],
  currency,
}: {
  school: SignupSchool;
  annualPriceCents: number | null;
  sportPrices?: SportPrice[];
  /** Sports whose price is the cheapest of several plans, shown as "from". */
  fromSports?: readonly string[];
  currency: string;
}) => {
  /** "$4,500 / year", or "from $1,500 / year" (`From` at the start of a line) for a plan-priced sport. */
  const priceText = (cents: number, sport: string | undefined, from: 'from' | 'From') => {
    const price = formatAnnualPrice(cents, currency);
    return sport && fromSports.includes(sport) ? `${from} ${price}` : price;
  };
  const details = describeSchool(school);
  const pricesDiffer =
    sportPrices.length > 1 &&
    (fromSports.length > 0 ||
      sportPrices.some((price) => price.annualPriceCents !== sportPrices[0].annualPriceCents));
  const singlePrice = sportPrices.length > 0 ? sportPrices[0].annualPriceCents : annualPriceCents;

  return (
    <div className="signup-school" data-testid="signup-school">
      <p className="signup-school__name">{school.name}</p>
      {details && <p className="signup-school__details">{details}</p>}
      {pricesDiffer ? (
        <ul className="signup-school__prices" data-testid="signup-price">
          {sportPrices.map((price) => (
            <li key={price.sport}>
              {sportLabel(price.sport)}: {priceText(price.annualPriceCents, price.sport, 'from')}
            </li>
          ))}
        </ul>
      ) : (
        singlePrice !== null && (
          <p className="signup-school__price" data-testid="signup-price">
            {priceText(singlePrice, sportPrices[0]?.sport, 'From')}
          </p>
        )
      )}
    </div>
  );
};
